import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'si-drop-down',
  imports: [MatFormFieldModule, CommonModule, MatSelectModule, FormsModule, ReactiveFormsModule],
  templateUrl: './si-drop-down.component.html',
  styleUrl: './si-drop-down.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SiDropDownComponent),
      multi: true
    }
  ]
})
export class SiDropDownComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  private readonly destroy$ = new Subject<void>();
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private externalDisabled = false;
  private formControlDisabled = false;

  @Input() inputValue: string | undefined | number = "";
  @Input() isReadMode: boolean = true;
  @Input() set isDisabled(value: boolean) {
    this.externalDisabled = value;
    this.syncDisabledState();
  }

  get isDisabled(): boolean {
    return this.externalDisabled || this.formControlDisabled;
  }

  @Input() dropDownList: Array<{label:string; value:string}> = [];
  @Output() inputValueChange = new EventEmitter<string>();

  multiselect: FormControl<string | null> = new FormControl('');

  ngOnInit(): void {
    this.multiselect.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const normalizedValue = value ?? '';
      this.inputValueChange.emit(normalizedValue);
      this.onChange(normalizedValue);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
 

  ngOnChanges(changes: { [key: string]: SimpleChange }): void {
    if (changes['inputValue']) {
      const value = changes['inputValue'].currentValue;
      if (value !== undefined && value !== null && value !== '') {
        this.multiselect.setValue(value.toString(), { emitEvent: false });
      } else {
        this.multiselect.setValue(null, { emitEvent: false });
      }
    }

    if (changes['isDisabled']) {
      this.syncDisabledState();
    }
  }

  writeValue(value: string | number | null): void {
    const normalizedValue = value === undefined || value === null || value === '' ? null : value.toString();
    this.multiselect.setValue(normalizedValue, { emitEvent: false });
    this.inputValue = normalizedValue ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDisabled = isDisabled;
    this.syncDisabledState();
  }

  private syncDisabledState(): void {
    if (this.isDisabled) {
      this.multiselect.disable({ emitEvent: false });
    } else {
      this.multiselect.enable({ emitEvent: false });
    }
  }

  onSelectionClosed(): void {
    this.onTouched();
  }

}
